// VULNERABLE: API service with insecure practices
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// VULNERABLE: Token stored in localStorage (should use httpOnly cookies)
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Authentication API
export const authAPI = {
  signup: async (userData) => {
    const response = await axios.post(`${API_BASE_URL}/auth/signup`, userData);
    // VULNERABLE: Storing token in localStorage
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  login: async (credentials) => {
    const response = await axios.post(`${API_BASE_URL}/auth/login`, credentials);
    // VULNERABLE: Storing sensitive data in localStorage
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },

  logout: async () => {
    try {
      await axios.post(`${API_BASE_URL}/auth/logout`, {}, {
        headers: getAuthHeader()
      });
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
  },

  getCurrentUser: () => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  // VULNERABLE: Reset password without proper verification
  resetPassword: async (email, newPassword) => {
    const response = await axios.post(`${API_BASE_URL}/auth/reset-password`, {
      email,
      newPassword
    });
    return response.data;
  }
};

// User API - VULNERABLE to BOLA
export const userAPI = {
  // VULNERABLE: Direct ID manipulation possible
  getUser: async (userId) => {
    const response = await axios.get(`${API_BASE_URL}/users/${userId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updateUser: async (userId, data) => {
    const response = await axios.put(`${API_BASE_URL}/users/${userId}`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getUserAccounts: async (userId) => {
    const response = await axios.get(`${API_BASE_URL}/users/${userId}/accounts`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getAllUsers: async () => {
    const response = await axios.get(`${API_BASE_URL}/users`, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Transaction API - VULNERABLE to BOLA
export const transactionAPI = {
  getTransactions: async (params) => {
    const response = await axios.get(`${API_BASE_URL}/transactions`, {
      params,
      headers: getAuthHeader()
    });
    return response.data;
  },

  getTransactionById: async (transactionId) => {
    const response = await axios.get(`${API_BASE_URL}/transactions/${transactionId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  createTransaction: async (data) => {
    const response = await axios.post(`${API_BASE_URL}/transactions`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Card API - VULNERABLE to BOLA and PCI exposure
export const cardAPI = {
  getUserCards: async (userId) => {
    const response = await axios.get(`${API_BASE_URL}/cards/user/${userId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getCardById: async (cardId) => {
    const response = await axios.get(`${API_BASE_URL}/cards/${cardId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  addCard: async (data) => {
    const response = await axios.post(`${API_BASE_URL}/cards`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  updateCard: async (cardId, data) => {
    const response = await axios.put(`${API_BASE_URL}/cards/${cardId}`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  deleteCard: async (cardId) => {
    const response = await axios.delete(`${API_BASE_URL}/cards/${cardId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Transfer API - VULNERABLE to BOLA
export const transferAPI = {
  createTransfer: async (data) => {
    const response = await axios.post(`${API_BASE_URL}/transfers`, data, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getTransferById: async (transferId) => {
    const response = await axios.get(`${API_BASE_URL}/transfers/${transferId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  },

  getUserTransfers: async (userId) => {
    const response = await axios.get(`${API_BASE_URL}/transfers/user/${userId}`, {
      headers: getAuthHeader()
    });
    return response.data;
  }
};

// Analytics API - VULNERABLE to BOLA
export const analyticsAPI = {
  getSpending: async (userId) => {
    const response = await axios.get(`${API_BASE_URL}/analytics/spending`, {
      params: { userId },
      headers: getAuthHeader()
    });
    return response.data;
  }
};

export default {
  authAPI,
  userAPI,
  transactionAPI,
  cardAPI,
  transferAPI,
  analyticsAPI
};
